import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Share, PlusSquare, Smartphone, MoreVertical } from 'lucide-react';

// Constants for install prompt behavior
const STORAGE_KEY_DISMISSED_TIME = 'installPromptDismissedTime';
const STORAGE_KEY_INSTALLED = 'appInstalledOnDevice';
const DISMISS_DURATION_HOURS = 24;

// Check if app is installed (standalone mode or previously marked as installed)
const checkIfInstalled = () => {
  // Check display-mode standalone (works when opened from home screen)
  const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
  
  // Check iOS standalone mode
  const isIOSStandalone = window.navigator.standalone === true;
  
  // Check if user previously installed (they clicked install button)
  const markedAsInstalled = localStorage.getItem(STORAGE_KEY_INSTALLED) === 'true';
  
  // Check if running as TWA (Trusted Web Activity) on Android
  const isTWA = document.referrer.includes('android-app://');
  
  return isStandaloneMode || isIOSStandalone || markedAsInstalled || isTWA;
};

export const InstallPrompt = () => {
  const location = useLocation();
  
  // Compute initial values outside of effects
  const getInitialStates = () => {
    const installed = checkIfInstalled();
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const safari = iOS && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);
    return { installed, iOS, safari };
  };
  
  const initialStates = getInitialStates();
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS] = useState(initialStates.iOS);
  const [isInstalled, setIsInstalled] = useState(initialStates.installed);
  const [isSafari] = useState(initialStates.safari);

  // Listen for app installed event
  useEffect(() => {
    const handleAppInstalled = () => {
      console.log('App was installed');
      localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
      setIsInstalled(true);
      setShowPrompt(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Also check on visibility change (user might have installed and returned)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (checkIfInstalled()) {
          setIsInstalled(true);
          setShowPrompt(false);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Don't show if already installed or not on home page
    if (location.pathname !== '/' || isInstalled) {
      return;
    }

    // Check if user dismissed within last 24 hours
    const dismissedTime = localStorage.getItem(STORAGE_KEY_DISMISSED_TIME);
    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < DISMISS_DURATION_HOURS) {
        return; // Still within 24-hour dismiss period
      }
      // 24 hours passed, clear the dismiss and show again
      localStorage.removeItem(STORAGE_KEY_DISMISSED_TIME);
    }

    // For Android/Desktop - capture the install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS - show custom prompt after 2 seconds on home page
    if (isIOS && !isInstalled) {
      const timer = setTimeout(() => {
        // Double-check installation status before showing
        if (!checkIfInstalled()) {
          setShowPrompt(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [location.pathname, isInstalled, isIOS]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Desktop - trigger native install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        // Mark as installed so prompt won't show again
        localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
        setIsInstalled(true);
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Just close the prompt, will show again on next home page visit
  };

  const handleDontShowAgain = () => {
    setShowPrompt(false);
    // Store the current timestamp - will expire after 24 hours
    localStorage.setItem(STORAGE_KEY_DISMISSED_TIME, Date.now().toString());
  };

  // Don't show if already installed or prompt not needed
  if (!showPrompt || isInstalled) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleDismiss}
      />
      
      {/* Prompt Modal - Compact for mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-auto overflow-hidden">
          {/* Header - Compact */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-base">Install Our App</h3>
                  <p className="text-orange-100 text-xs">Quick access from home screen</p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-1.5 hover:bg-orange-600 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Content - Compact */}
          <div className="p-3">
            {isIOS ? (
              /* iOS Instructions - Compact */
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">
                  To install on your iPhone/iPad:
                </p>
                {isSafari ? (
                  /* Safari Instructions - Compact */
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
                        <MoreVertical className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>1.</strong> Tap <strong>browser menu</strong> (three dots)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center flex-shrink-0">
                        <Share className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>2.</strong> Tap <strong>Share</strong> button
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-800 rounded-md flex items-center justify-center flex-shrink-0">
                        <PlusSquare className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>3.</strong> Scroll &amp; tap <strong>&quot;Add to Home Screen&quot;</strong>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>4.</strong> Tap <strong>&quot;Add&quot;</strong> to confirm
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Chrome/Other iOS Browser Instructions - Compact */
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center flex-shrink-0">
                        <Share className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>1.</strong> Tap <strong>Share</strong> (right of URL bar)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-800 rounded-md flex items-center justify-center flex-shrink-0">
                        <PlusSquare className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>2.</strong> Scroll &amp; tap <strong>&quot;Add to Home Screen&quot;</strong>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>3.</strong> Tap <strong>&quot;Add&quot;</strong> to confirm
                      </p>
                    </div>
                    <p className="text-xs text-amber-600 bg-amber-50 p-1.5 rounded mt-2">
                      💡 For best experience, use Safari
                    </p>
                  </div>
                )}
                <button
                  onClick={handleDismiss}
                  className="w-full mt-3 py-2.5 bg-gray-800 text-white font-semibold rounded-xl hover:bg-gray-900 transition-colors text-sm"
                >
                  Got it!
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
                    setIsInstalled(true);
                    setShowPrompt(false);
                  }}
                  className="w-full mt-2 py-2 text-orange-600 text-xs font-medium hover:text-orange-700"
                >
                  I've already installed it ✓
                </button>
              </div>
            ) : (
              /* Android/Desktop Install Button */
              <div>
                <button
                  onClick={handleInstall}
                  className="w-full py-2.5 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Install App Now
                </button>
              </div>
            )}
            
            <button
              onClick={handleDismiss}
              className="w-full mt-2 py-1.5 text-gray-500 text-xs hover:text-gray-700"
            >
              Maybe later
            </button>
            <button
              onClick={handleDontShowAgain}
              className="w-full py-1 text-gray-400 text-xs hover:text-gray-600"
            >
              Don&apos;t show again
            </button>
          </div>
        </div>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};
